import { useCallback } from "react";
import { cn } from "@edusogno/lib/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSetNotificationMetadataAsSeen } from "@/data/user";
import {
  NotificationType,
  UserNotificationItem,
  UserNotificationPaginator,
} from "@/types";
import dayjs from "dayjs";
import Button from "@edusogno/primitives/button";
import EyeIcon from "../icons/EyeIcon";
import WebIcon from "../icons/WebIcon";
import PDFFileIcon from "@/components/icons/PDFFileIcon.tsx";
import API_ENDPOINTS from "@/data/client/endpoints";
import VideoFileIcon from "@/components/icons/VideoFileIcon.tsx";
import MaterialsBadge from "./MaterialsBadge";
import NewNotificationIcon from "../icons/NewNotificationIcon";

interface MaterialProps {
  id: number;
  tk: string;
  name: string;
  type: string;
  isNew?: boolean | null;
  subtype: string;
  fileType: string;
  lessonOf: string;
  resource?: string;
  isPrecourse?: boolean | null;
  materialType: string;
  images?: string[] | null;
}

const MaterialItem = ({
  id,
  tk,
  name,
  type,
  isNew,
  subtype,
  fileType,
  lessonOf,
  resource,
  isPrecourse,
  materialType,
  images,
}: MaterialProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { mutateAsync } = useSetNotificationMetadataAsSeen();

  const navigate = useNavigate();

  const viewFile = useCallback(async () => {
    const queryResults = queryClient.getQueryData<UserNotificationPaginator>([
      API_ENDPOINTS.NOTIFICATIONS,
    ]);

    const notifications = (queryResults?.items as UserNotificationItem[]) || [];

    const notification = notifications.find(
      (n) =>
        n.action === NotificationType.ADD_MATERIALS &&
        n.metadata.some((meta) => meta.id === id && !meta.seen),
    );

    if (notification) {
      const metadata = notification.metadata.find((meta) => meta.id === id);
      if (metadata) {
        await mutateAsync({
          id_notification: notification.id,
          id_metadata: metadata.id,
        });

        queryClient.setQueryData<UserNotificationItem[]>(
          [API_ENDPOINTS.NOTIFICATIONS_SET_SEEN],
          (old) =>
            old?.map((n) =>
              n.id === notification.id
                ? {
                  ...n,
                  metadata: n.metadata.map((meta) =>
                    meta.id === metadata.id ? { ...meta, seen: true } : meta,
                  ),
                }
                : n,
            ),
        );
      }
    }

    if (type === "link" && !!resource) {
      return window.open(resource, "_blank");
    }

    navigate("./file", {
      state: {
        name,
        lessonOf,
        tk: tk,
        type: materialType,
        subtype: subtype,
        fileType: fileType,
        images: images,
      },
    });
  }, [
    id,
    tk,
    type,
    name,
    subtype,
    resource,
    navigate,
    fileType,
    lessonOf,
    mutateAsync,
    queryClient,
    materialType,
    images,
  ]);

  const handleClick = () => {
    viewFile()
      .then(() => { })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <div className="flex flex-row items-center justify-between w-full px-6 py-4 bg-white rounded-md lg:gap-5 center">
      <div className="flex-row flex items-center gap-5 w-[85%]">
        <div className="relative">
          {isNew && (
            <NewNotificationIcon className="absolute w-[6px] h-[6px] -top-[2px] -left-[6px]" />
          )}

          {fileType === "pdf" ? (
            <PDFFileIcon className="w-8 min-w-[32px] h-auto" />
          ) : fileType === "video" ? (
            <VideoFileIcon className="w-8 min-w-[32px] h-auto" />
          ) : (
            <WebIcon className={"w-8 min-w-[32px] h-auto"} />
          )}
        </div>

        <div className="until-lg:hidden">
          <MaterialsBadge type={materialType} subtype={subtype} />
        </div>

        <div className="flex flex-col">
          {isPrecourse ? (
            <p className={cn("text-xs text-primary")}>
              {t("materials.precourse")}
            </p>
          ) : (
            <p className="text-[--primary] text-xs">
              {t("materials.lesson_of")}{" "}
              <b className="font-semibold capitalize">
                {dayjs(lessonOf, "YYYY-MM-DD")
                  .format("ddd MM/DD/YYYY")
                  .split(".")
                  .join(" ")}
              </b>
            </p>
          )}

          <p className="text-[--secondary] font-semibold text-base line-clamp-1">
            {name}
          </p>
        </div>
      </div>

      <Button onClick={handleClick} variant="ghost" className="w-fit lg:hidden">
        <EyeIcon className="w-6 h-auto fill-brand-secondary" />
      </Button>

      <Button
        size="sm"
        color="primary"
        onClick={handleClick}
        className="hidden lg:block"
      >
        {t("btns.view")}
      </Button>
    </div>
  );
};

export default MaterialItem;
